import { Check, X } from 'lucide-react'

import { type DictationControls, DictationStatus } from '@/hooks/useDictation'
import { Button } from './button'

// How much each bar of the meter moves with the voice, so that they don't move as one.
const METER = [0.5, 0.8, 1, 0.7, 0.45]
// The end of what was said, which is what fits beside the meter.
const HEARD_CHARS = 80

/** A dictation while it runs: what was heard so far, how loud, and buttons to stop it. */
export function DictationBar({ dictation }: { dictation: DictationControls }) {
  const { text, loading, status, level } = dictation
  const finishing = status === DictationStatus.FINISHING
  const progress =
    loading !== null ? 'Getting dictation ready…' : finishing ? 'Writing it down…' : 'Listening…'

  return (
    <div data-slot="dictation-bar" className="flex min-w-0 flex-1 items-center gap-2">
      <Button
        variant="ghost"
        size="icon-sm"
        type="button"
        className="text-ink-400 hover:text-ink rounded-full"
        aria-label="Cancel dictation"
        title="Cancel dictation"
        // Takes the focus from the microphone, which this bar replaces.
        autoFocus
        onClick={dictation.cancel}
      >
        <X aria-hidden="true" />
      </Button>
      {/* ph-no-capture: replay masks the message box, not this copy of what is being said into it. */}
      <span className="ph-no-capture text-ink-500 min-w-0 flex-1 truncate text-base leading-6">
        {(text.length > HEARD_CHARS ? `…${text.slice(-HEARD_CHARS)}` : text) ||
          (loading !== null ? `${progress} ${loading}%` : progress)}
      </span>
      {/* Only the step is read out: reading each word heard would talk over the speaker, and into the microphone. */}
      <span className="sr-only" aria-live="polite">
        {progress}
      </span>
      <span className="flex h-5 items-center gap-0.5" aria-hidden="true">
        {METER.map((weight, index) => (
          <i
            key={index}
            className="bg-brand-600 h-full w-0.75 rounded-full transition-transform duration-75"
            style={{ transform: `scaleY(${0.2 + Math.min(1, level * weight) * 0.8})` }}
          />
        ))}
      </span>
      <Button
        size="icon"
        type="button"
        className="size-8 rounded-full"
        aria-label="Done dictating"
        title="Done dictating"
        disabled={status !== DictationStatus.LISTENING}
        loading={finishing ? <span className="sr-only">Writing it down…</span> : undefined}
        onClick={() => void dictation.finish()}
      >
        <Check aria-hidden="true" />
      </Button>
    </div>
  )
}
